import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withCoreComponent, withUserAgent } from 'core/hocs';
import { GameWinnerList as GameWinnerListCore, FormattedTag } from 'core/components';
import { isEmptyOrNil, getStaticUrl } from 'core/helpers';
import { ImageLazyLoad, LAZY_LOAD_TYPES } from 'components/image-lazy-load/image-lazy-load';
import { Carousel } from 'components/carousel/carousel';
import { formatAmountWithCurrency } from 'helpers/numbers';
import { setUsernameHidden } from 'helpers/username';
import './game-winner-list.scss';
import { DEVICES_TYPES } from 'core/constants';

const DEFAULT_THUMB_PATH = `${getStaticUrl()}/images/default-game-bg/thumb.jpg`;

const carouselSettings = {
  arrows: false,
  responsive: [
    {
      breakpoint: 445,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        variableWidth: true,
      },
    },
    {
      breakpoint: 719,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 2,
        variableWidth: true,
      },
    },
    {
      breakpoint: 1023,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 2,
        variableWidth: true,
      },
    },
  ],
};

class GameWinnerCarouselUI extends Component {
  static propTypes = {
    locale: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape([])),
    isInProgress: PropTypes.bool.isRequired,
    openGame: PropTypes.func.isRequired,
    deviceType: PropTypes.string.isRequired,
    withStubs: PropTypes.bool,
  }

  static defaultProps = {
    items: [],
    withStubs: false,
  }

  state = {
    showSlider: false,
  };

  componentDidMount() {
    this.breakpointFound = this.findBreakpoint(carouselSettings);

    this.setState({ showSlider: true });
  }

  findBreakpoint = (settings) => {
    if (typeof window !== 'undefined') {
      return settings.responsive.find(({ breakpoint }) => window.innerWidth <= breakpoint);
    }

    return null;
  };

  prepareWinnersList = () => {
    const { items: winners } = this.props;

    const foundBreakpoint = this.breakpointFound;

    const slidesToShow = foundBreakpoint && foundBreakpoint.settings.slidesToShow
      ? Math.floor(foundBreakpoint.settings.slidesToShow)
      : carouselSettings.slidesToShow;

    if (winners.length < slidesToShow) { // workaround for a case when number of items < slidesToShow (react-slick bug)
      const stubs = new Array(slidesToShow - winners.length).fill({ isStub: true });

      return [...winners, ...stubs];
    }

    return winners;
  };

  openGame = ({
    currentTarget: {
      dataset: { id: gameId, producer },
    },
  }) => {
    const { openGame } = this.props;
    openGame({ gameId, producer });
  };

  render() {
    const {
      locale,
      items,
      withStubs,
      isInProgress,
      deviceType,
    } = this.props;
    const { showSlider } = this.state;
    const isMobile = deviceType === DEVICES_TYPES.MOBILE;

    const preparedItemsList = withStubs ? this.prepareWinnersList() : items;

    if (!isMobile) {
      return null;
    }

    return (
      !isEmptyOrNil(items) && !isInProgress && showSlider && (
        <div className="game-winner-carousel bg-orange border-bottom border-top border-gray-10-20">
          <Carousel settings={{
            ...carouselSettings,
            ...this.state.sliderSettings,

          }}
          >
            {preparedItemsList.map(({
              gameId,
              producer,
              playerName,
              amountWon,
              currency,
              title,
              thumbPath,
              isStub,
            }, idx) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${gameId}-${producer}-${idx}`}
                className={classNames(
                  'game-winner-carousel-item d-flex py-1', {
                    'no-divider': isStub,
                  }
                )}
              >
                {!isStub && (
                  <>
                    <ImageLazyLoad
                      src="/assets/public/images/transparent-pixel.png"
                      className="game-winner-carousel-image flex-shrink-0"
                      classNameLoaded="game-winner-carousel-loaded"
                      classNameError="game-winner-carousel-error"
                      srcLazy={thumbPath}
                      type={LAZY_LOAD_TYPES.BACKGROUND}
                      srcFallback={DEFAULT_THUMB_PATH}
                    >
                      <div
                        className="game-winner-carousel-overlay d-flex justify-content-center align-items-center h-100"
                        role="button"
                        tabIndex="0"
                        data-id={gameId}
                        data-producer={producer}
                        onClick={this.openGame}
                        onKeyPress={this.openGame}
                      />
                    </ImageLazyLoad>

                    <div className="d-flex justify-content-between align-items-center overflow-hidden flex-grow-1 ml-2">
                      <div className="d-flex flex-column text-black game-card-description text-small">
                        <div className="d-flex">
                          <span className="text-capitalize mr-0_5 text-white">{setUsernameHidden(playerName, true)}</span>
                          <FormattedTag id="won" className="mr-0_5 text-white" />
                          <span className="font-weight-bold text-yellow-primary">
                            {formatAmountWithCurrency({
                              amount: amountWon, currency, locale, isIsoFormat: true
                            })}
                          </span>
                        </div>

                        <div className="d-flex">
                          <FormattedTag id="in" className="mr-0_5 text-white" />

                          <span
                            className="text-truncate text-white font-weight-bold"
                            title={title}
                            role="button"
                            tabIndex="0"
                            data-id={gameId}
                            data-producer={producer}
                            onClick={this.openGame}
                            onKeyPress={this.openGame}
                          >
                            {title}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </Carousel>
        </div>
      )
    );
  }
}

export const GameWinnerCarousel = withCoreComponent(GameWinnerListCore, withUserAgent(GameWinnerCarouselUI));
